import { memo, useCallback, useContext, useMemo, MouseEvent } from 'react';
import cn from 'classnames';
import { IconClose } from '@icons';
import { ByoTableRow } from "../byoTable.helpers";
import { GlobalContext } from '@components/App/App.context';
import CanvasChart from '@components/CanvasChart/CanvasChart';
import Icon from '@components/Icon/Icon';
import { useByoDataContext } from '@pages/Byo/ByoContext';
import KeywordCellSettings from './KeywordCellSettings';
import KeywordCellMenuDropdown from './KeywordCellMenuDropdown';
import './KeywordCell.scss';
import { useSelector } from 'react-redux';
import { useAiSearchSettingsContext } from '@vms/AiSearchSettingsContext/AiSearchSettingsContext';

interface KeywordCellProps {
    keyword: ByoTableRow & { isSelected?: boolean };
    selectKeyword: (keywordName?: string | null) => void;
    toggleColorSelection: (keywordName: string) => void;
    openMenuExamples: () => void;
}

const KeywordCell = ({
    keyword,
    selectKeyword,
    toggleColorSelection,
    openMenuExamples,
}: KeywordCellProps) => {
    const { getRem } = useContext(GlobalContext);
    const { removeItem } = useByoDataContext();
    const { resetSearch } = useAiSearchSettingsContext();

    const isLightTheme = useSelector((state: any) => state.app.isThemeLightEnable);

    const defaultColor = isLightTheme ? '#788390' : '#fff';

    const chartData = useMemo(() => {
        const transformedData = keyword.chartData.map((item) => ({
            label: Number(item.name),
            values: [item.valueNumeric],
        }));

        return {
            hasPrediction: false,
            chartsCount: 1,
            chartInfo: [{ name: '', color: keyword.color || defaultColor }],
            data: transformedData,
        };
    }, [keyword.color, keyword.chartData, defaultColor]);
    
    const handleRemovedKeyword = useCallback(() => {
        removeItem(keyword.name ?? '');
        resetSearch();
    }, [removeItem, keyword.name]);

    const handleSelectKeyword = useCallback(() => {
        selectKeyword(keyword.isSelected ? '' : keyword.name);
    }, [selectKeyword, keyword.isSelected, keyword.name]);


    const handlePaintChart = useCallback(
        (event: MouseEvent) => {
            event.stopPropagation();
            toggleColorSelection(keyword.name ?? '');
        },
        [toggleColorSelection, keyword]
    );

    return (
        <div className={cn("KeywordCell", { "is-selected": keyword.isSelected })}>
            <div className='KeywordCell__content' onClick={handleSelectKeyword}>
                <div
                    className='KeywordCell__chart'
                    onClick={handlePaintChart}
                >
                    <CanvasChart
                        activeTabId={1}
                        chartDataType={1}
                        width={getRem(100)}
                        height={getRem(80)}
                        coefficient={getRem(80) / getRem(1350)}
                        charts={chartData}
                        hasPrediction={false}
                        isLightTheme={false}
                        onlyLine
                        currentCurrency="USD"
                    />
                </div>
                <div
                    className={cn(
                        'KeywordCell__text',
                        !keyword.isTag && 'is-light',
                        keyword.isTag && 'tag-label',
                    )}
                >
                    {keyword.name?.toUpperCase()}
                </div>
            </div>

            <div className="KeywordCell__actions">
                <KeywordCellMenuDropdown keyword={keyword} openMenuExamples={openMenuExamples} />

                <button className="KeywordCell__remove-btn" onClick={handleRemovedKeyword}>
                    <Icon type='small'>
                        <IconClose />
                    </Icon>
                </button>

                <KeywordCellSettings
                    id={keyword.id ?? 0}
                    name={keyword.name ?? ''}
                    wordExtensions={keyword.wordExtensions}
                />
            </div>
        </div>
    );
};

export default memo(KeywordCell);
